import { Namespace, TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { SectionButtonHeader } from "@/components/Interface/SectionButtonHeader/SectionButtonHeader";
import { useCustomerData } from "@/components/Onboarding/Steps/PersonalDetails/useCustomerState";
import { TextContainer } from "@/components/Portal/UserAccount/PersonalDetails/TextContainer/TextContainer";
import { useAlert } from "@/hooks/useAlert";
import { useTwoFactorMethodFromQueryParam } from "@/hooks/useTwoFactorMethodFromQueryParam.ts";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { authenticate } from "@/services/auth";
import {
  BasicCustomerDataTwoFactorMethod,
  ValidateTwoFactorAuthenticationMethod,
} from "@/services/model";
import { FCC } from "@/types";
import { AlertTypes } from "@/utils/atoms";
import { TWO_FACTOR_ERROR_MESSAGES } from "@/utils/twoFactorErrorHandler";

import { TwoFactorCodeModal } from "./TwoFactorCodeModal";

export const expectedAuthTypeQueryParam = "expectedAuthType";

/**
 * Displays the current 2fa status and a link to change that status.
 * Determines status by combining query parameters (we might expect a change that the backend does not return yet)
 * and the backend state.
 */
export const TwoFactorAuthStatus: FCC = () => {
  const { t } = useTranslation();
  const twoFactorMethod = useTwoFactorMethodFromQueryParam();
  const { customerData } = useCustomerData();

  const showAlert = useAlert();

  const [showModal, setShowModal] = useState(false);
  const [hasRequestedSms, setHasRequestedSms] = useState(false);
  const [twoFactorToken, setTwoFactorToken] = useState<string | undefined>(
    undefined,
  );
  const hasAnyTwoFactorAuth =
    twoFactorMethod !== BasicCustomerDataTwoFactorMethod.STANDARD;

  const twoFactorText = getTwoFactorAuthText(t, twoFactorMethod);
  const twoFactorCodeRedirect = useTwoFactorCodeRedirect();

  return (
    <div className="flex flex-col gap-6 md:min-w-[464px]">
      <div>
        <h3 className="font-semibold text-secondary-100">
          {t("portal:user-account.security.2fa.title")}
        </h3>
        <p className="text-base">
          <span>{t("portal:user-account.security.2fa.description")}</span>
          <a
            className="ml-2 text-primary-100 underline"
            href={t("portal:user-account.security.2fa.readMoreUrl")}
            target="_blank"
            rel="noreferrer"
          >
            {t("portal:user-account.security.2fa.readMore")}
          </a>
        </p>
      </div>
      <div>
        <SectionButtonHeader
          header={t("portal:user-account.security.2fa.header")}
        >
          {showModal && (
            <TwoFactorCodeModal
              userName={customerData?.email}
              setShowModal={setShowModal}
              method={twoFactorMethod}
              successAction={twoFactorCodeRedirect}
              type="change_2fa"
              twoFactorGrantToken={twoFactorToken}
            />
          )}

          {hasAnyTwoFactorAuth ? (
            <button
              type="button"
              className="text-primary-100 underline"
              onClick={() => {
                if (hasRequestedSms) {
                  setShowModal(true);

                  return;
                }
                authenticate()
                  .then((response) => {
                    setTwoFactorToken(response?.tempToken);
                    setHasRequestedSms(true);
                    setShowModal(true);
                  })
                  .catch(() => {
                    showAlert({
                      text: t(TWO_FACTOR_ERROR_MESSAGES.SERVER_ERROR),
                      type: AlertTypes.error,
                    });
                  });
              }}
            >
              {t("common.buttons.change")}
            </button>
          ) : (
            <Link to={portalRoutes.user.security.initialSetup}>
              {t("common.buttons.activate")}
            </Link>
          )}
        </SectionButtonHeader>
        <TextContainer>{twoFactorText}</TextContainer>
      </div>
    </div>
  );
};

const getTwoFactorAuthText = (
  t: TFunction<Namespace, undefined, Namespace>,
  authType?: BasicCustomerDataTwoFactorMethod,
) => {
  if (authType === BasicCustomerDataTwoFactorMethod.APP) {
    return t("portal:user-account.security.2fa.method.app");
  } else if (authType === BasicCustomerDataTwoFactorMethod.SMS) {
    return t("portal:user-account.security.2fa.method.sms");
  }
};

/**
 * Returns a function to handle navigation based on the two-factor
 * authentication method specified in the query parameters. Depending on the method (SMS or App),
 * it will navigate to the relevant 2FA change page.
 */
const useTwoFactorCodeRedirect = () => {
  const navigate = useNavigate();
  const method = useTwoFactorMethodFromQueryParam();

  return () => {
    const hasSmsTwoFactorMethod =
      method === ValidateTwoFactorAuthenticationMethod.SMS;
    const hasAppTwoFactorMethod =
      method === ValidateTwoFactorAuthenticationMethod.APP;

    if (hasAppTwoFactorMethod) {
      navigate(portalRoutes.user.security.change2faApp);
    }
    if (hasSmsTwoFactorMethod) {
      navigate(portalRoutes.user.security.change2faSms);
    }
  };
};
